import {
    useListRunnerSCMIntegrations,
    type PlainHostAuthenticationToken,
} from "@/queries/runner-configuration-queries";
import { useRunner } from "@/queries/runner-queries";
import { useState, useCallback, type FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { DeleteGitAuthenticationDialog } from "./DeleteGitAuthenticationDialog";
import type { ServiceAccountHeaders } from "@/api";
import { ServiceAccountGitAuthenticationDetails } from "@/components/settings/organization/service-accounts/ServiceAccountGitAuthenticationDetails";

export type ServiceAccountGitAuthenticationCardProps = {
    token: PlainHostAuthenticationToken;
    headers: ServiceAccountHeaders;
    onDelete?: () => void;
};

export const ServiceAccountGitAuthenticationCard: FC<ServiceAccountGitAuthenticationCardProps> = ({
    token,
    headers,
}) => {
    const { data: runner } = useRunner(token.runnerId);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    // The PlainHostAuthenticationToken doesn't contain the SCM Integration ID so we have to
    // list all SCM integrations and find the correct one by matching the host.
    const { data: scmIntegrations } = useListRunnerSCMIntegrations(token.runnerId, { headers });
    const scmIntegration = scmIntegrations?.find((integration) => integration.host === token.host);

    const handleRemove = useCallback(() => {
        setShowDeleteDialog(true);
    }, []);

    return (
        <>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border-subtle px-3 py-4">
                <ServiceAccountGitAuthenticationDetails token={token} runner={runner} scmIntegration={scmIntegration} />
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRemove}
                    data-testid={`remove-git-authentication-${token.id}`}
                    data-tracking-id="remove-git-authentication"
                >
                    Remove
                </Button>
            </div>
            <DeleteGitAuthenticationDialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
                token={token}
                scmIntegration={scmIntegration}
                runner={runner}
                headers={headers}
            />
        </>
    );
};
